// generatedViewStyleLint.js — two style mistakes a generated ANSWER VIEW makes
// that render NOTHING, caught before it mounts (GENERATED-VIEW-STYLE-LINT-1).
//
// Measured on dev 2026-10-04 (Storm, render_answer_ui "treasury accounts with
// share bars"): every bar track was `height: 8` and `background: 'surface'`.
// smbls emits a bare number as is (`height: 8` is not a CSS length, so the
// browser drops the declaration — the track was 0 px tall) and an unknown
// colour name as is (`surface` is no colour, so the track was transparent).
// The containment gate accepted the view and said nothing; the share column
// showed percentages and no bars.
//
//   unitless-size  a length prop (width, height, padding, gap, round, …) set to
//                  a bare non-zero number or numeric string — use a spacing
//                  token ('A', 'B2', 'X') or a CSS length with its unit.
//   grid-track     a grid track list (gridTemplateColumns, columns, rows, …)
//                  that is not a string — a bare number (`columns: 3`), an
//                  array or an object (`columns: [{ size: '2fr', key: … }]`) is
//                  no track list, so the grid falls back to ONE column. Measured
//                  on dev 2026-10-05: "three tiles side by side" drew three
//                  stacked rows (columns: 3); a treasury table stacked every
//                  cell (an invented columns array). Write a track string:
//                  'repeat(3, 1fr)', '2fr 1fr 1fr 2fr'.
//   unknown-css-var  a style value that reads a CSS variable the page does not
//                  define — `var(--cerulean-9)`, `--ink-alpha-60` (measured on
//                  dev 2026-10-05: the treasury bars moved into `css: {
//                  background: 'var(--cerulean-9)' }`, an invented variable,
//                  and drew nothing). Judged only with a resolver for the live
//                  page's variables (the real ones read --color-<name>,
//                  --spacing-<token>).
//   unknown-theme  `theme: '<name>'` that is not a theme of the LIVE design
//                  system — 'caption' / 'paragraph' are COLOURS (color:
//                  'caption'), not themes, and a missing theme is ignored.
//   size-word      `size` / `boxSize` / `minSize` / `maxSize` set to a TYPE-size
//                  word ('xl', 's', 'lg', 'small', …). In DOMQL `size` is a BOX
//                  size (css-in-props block.js: inline-size + block-size), never
//                  a type size. Measured on dev 2026-10-15 (Keepz DEV, "Show me
//                  payment pay_09lUkppFn"): `Hgroup.H: { size: 'xl' }` drew
//                  35px type in an 8.3px-tall box, and the title's two lines
//                  overlapped the line under it.
//   grid-cols      `cols` on a grid-like node (extends Grid…, or display grid).
//                  `cols` is no DOMQL prop, so it is ignored and the grid draws
//                  ONE column. Measured on dev 2026-10-15 (Keepz DEV, "Show me
//                  payment pay_09lUkppFn"): `{ extends: 'Grid', cols: 2 }` put
//                  every label on its own row above its value. The track props
//                  are `columns` / `templateColumns` / `gridTemplateColumns`
//                  (css-in-props grid.js). A textarea's HTML `cols` is left be.
//   palette-fill   `background` / `backgroundColor` set to a PALETTE COLOUR on an
//                  element that carries TEXT and names no ink of its own. The
//                  theme owns the background AND the colour, so a palette fill
//                  replaces the background and leaves the theme's ink over it.
//                  Measured on dev 2026-10-05 (Storm, "a small answer view that
//                  summarizes fleet health"): `{ theme: 'card', backgroundColor:
//                  'cerulean-10' }` painted rgb(0,178,245) under the card
//                  theme's own ink rgb(188,188,194) — the body text read 1.3:1
//                  and the title 2.4:1 (AA asks 4.5:1 / 3:1). A theme whose own
//                  background IS that fill still pairs it, and an element that
//                  sets its own `color` (or a child that does) is lit; the
//                  design system's neutral surface roles are not accents.
//   unknown-icon   an Icon `name` / `icon` the live design system does not
//                  register — resolved exactly the way the framework resolves
//                  it (default-config components/Icon applyIconRender: the
//                  variant-suffix join, `toCamelCase`, then the two leading
//                  segments), so a name that paints is never refused. Measured
//                  on the same view: `name: 'symbols/support'` — a library
//                  prefix the resolver never strips — drew the `noIcon`
//                  placeholder and warned `[Icon] Unknown icon name`. Judged
//                  only with an icon table and no `iconsLoader` (which may
//                  register a name late), never a guess.
//   unknown-color  a colour prop (color, background, borderColor, fill, …) whose
//                  value is not a colour of the LIVE design system (its colours
//                  and, for backgrounds, its gradients), not a CSS colour (a
//                  named colour, #hex, rgb()/hsl()/…, var(), a gradient) and not
//                  a keyword. With no design system to read, colours are not
//                  judged (never a guess).
//
// Pure: (def, designSystem) → violations in the containment gate's shape
// ({ rule, at, detail }). Dynamic values (functions) are not judged; `state`,
// `data`, `attr`, `metadata` and `scope` bags are data, never style; the
// children of a `childrenAs: 'state'` list are data rows.

const LENGTH_PROPS = new Set([
  'width',
  'height',
  'minWidth',
  'minHeight',
  'maxWidth',
  'maxHeight',
  'boxSize',
  'widthRange',
  'heightRange',
  'padding',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'paddingInline',
  'paddingBlock',
  'margin',
  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft',
  'marginInline',
  'marginBlock',
  'gap',
  'rowGap',
  'columnGap',
  'top',
  'right',
  'bottom',
  'left',
  'inset',
  'round',
  'borderRadius',
  'borderWidth',
  'fontSize',
  'letterSpacing',
  'flexBasis',
  'outlineWidth',
  'outlineOffset'
])

const COLOR_PROPS = new Set([
  'color',
  'background',
  'backgroundColor',
  'borderColor',
  'borderTopColor',
  'borderRightColor',
  'borderBottomColor',
  'borderLeftColor',
  'outlineColor',
  'fill',
  'stroke',
  'caretColor',
  'accentColor'
])

const TRACK_PROPS = new Set([
  'gridTemplateColumns',
  'gridTemplateRows',
  'gridAutoColumns',
  'gridAutoRows',
  'columns',
  'rows',
  'templateColumns',
  'templateRows',
  'autoColumns',
  'autoRows'
])

const BOX_SIZE_PROPS = new Set(['size', 'boxSize', 'minSize', 'maxSize'])
// A type-size word, lowercase (spacing tokens are capitals: 'L', 'C1', 'A B').
const TYPE_SIZE_WORD = /^(x{0,3}s|x{0,3}l|[2-9]x[sl]|sm|md|lg|m|small|medium|large|tiny|huge|big)$/

// …unless the live design system registers that word as a `sizes` token:
// then it IS a box size (scratch getSizeValue looks the name up as written,
// then lower-cased, before the spacing sequence).
const _sizeWord = (v, sizes) =>
  typeof v === 'string' &&
  v
    .trim()
    .split(/\s+/)
    .some(
      (part) =>
        TYPE_SIZE_WORD.test(part) &&
        !(sizes && (sizes[part] !== undefined || sizes[part.toLowerCase()] !== undefined))
    )

// A grid-like node: it extends a Grid component, or sets display: grid.
const _gridLike = (node) =>
  node.display === 'grid' ||
  node.display === 'inline-grid' ||
  _extendsNames(node.extends).some((n) => /^Grid/.test(n))

const DATA_BAGS = new Set(['state', 'data', 'attr', 'metadata', 'scope'])

const CSS_COLOR_KEYWORDS = new Set([
  'transparent',
  'currentcolor',
  'inherit',
  'initial',
  'unset',
  'revert',
  'none'
])

// CSS Color Module level 4 named colours.
const CSS_NAMED_COLORS = new Set(
  (
    'aliceblue antiquewhite aqua aquamarine azure beige bisque black blanchedalmond blue ' +
    'blueviolet brown burlywood cadetblue chartreuse chocolate coral cornflowerblue cornsilk ' +
    'crimson cyan darkblue darkcyan darkgoldenrod darkgray darkgreen darkgrey darkkhaki ' +
    'darkmagenta darkolivegreen darkorange darkorchid darkred darksalmon darkseagreen ' +
    'darkslateblue darkslategray darkslategrey darkturquoise darkviolet deeppink deepskyblue ' +
    'dimgray dimgrey dodgerblue firebrick floralwhite forestgreen fuchsia gainsboro ghostwhite ' +
    'gold goldenrod gray green greenyellow grey honeydew hotpink indianred indigo ivory khaki ' +
    'lavender lavenderblush lawngreen lemonchiffon lightblue lightcoral lightcyan ' +
    'lightgoldenrodyellow lightgray lightgreen lightgrey lightpink lightsalmon lightseagreen ' +
    'lightskyblue lightslategray lightslategrey lightsteelblue lightyellow lime limegreen linen ' +
    'magenta maroon mediumaquamarine mediumblue mediumorchid mediumpurple mediumseagreen ' +
    'mediumslateblue mediumspringgreen mediumturquoise mediumvioletred midnightblue mintcream ' +
    'mistyrose moccasin navajowhite navy oldlace olive olivedrab orange orangered orchid ' +
    'palegoldenrod palegreen paleturquoise palevioletred papayawhip peachpuff peru pink plum ' +
    'powderblue purple rebeccapurple red rosybrown royalblue saddlebrown salmon sandybrown ' +
    'seagreen seashell sienna silver skyblue slateblue slategray slategrey snow springgreen ' +
    'steelblue tan teal thistle tomato turquoise violet wheat white whitesmoke yellow yellowgreen'
  ).split(' ')
)

const CSS_COLOR_SYNTAX =
  /^(--|var\(|#|rgba?\(|hsla?\(|hwb\(|lab\(|lch\(|oklab\(|oklch\(|color\(|color-mix\(|(repeating-)?(linear|radial|conic)-gradient\(|url\()/i

const _isBareNumber = (v) =>
  (typeof v === 'number' && Number.isFinite(v) && v !== 0) ||
  (typeof v === 'string' && /^\s*-?\d+(\.\d+)?\s*$/.test(v) && Number(v) !== 0)

// A track list is a string of tracks (or computed by a function); a number, a
// numeric string, an array or an object never is.
const _isBadTrack = (v) =>
  (typeof v === 'number' && Number.isFinite(v)) ||
  (typeof v === 'string' && /^\s*\d+(\.\d+)?\s*$/.test(v)) ||
  Array.isArray(v) ||
  (!!v && typeof v === 'object')

const _keysOf = (bag) =>
  bag && typeof bag === 'object' && !Array.isArray(bag) ? Object.keys(bag) : []

// The live design system's colour and gradient names, or null when there is
// no colour table to judge against.
export const designSystemColorNames = (ds) => {
  if (!ds || typeof ds !== 'object') return null
  const colors = _keysOf(ds.color || ds.COLOR)
  if (!colors.length) return null
  return {
    colors: new Set(colors),
    gradients: new Set(_keysOf(ds.gradient || ds.GRADIENT))
  }
}

// smbls's own colour-token grammar (scratch utils/color.js COLOR_TOKEN_REGEX):
// `name[.alpha][+N|-N|=N]` with NO spaces — 'purple.2' (alpha), 'purple-10' /
// 'purple+10' (shade), 'line-highlight' (a hyphenated name). A spaced modifier
// ('blue .2') does not parse and is dropped, like an unknown name.
const SMBLS_COLOR_TOKEN = /^([a-zA-Z]\w*(?:-[a-zA-Z]\w*)*)(?:\.(\d+))?(?:([+-]\d+|=\d+))?$/

const _isKnownColor = (prop, value, names) => {
  const s = value.trim()
  if (!s || CSS_COLOR_SYNTAX.test(s)) return true
  const lower = s.toLowerCase()
  if (CSS_COLOR_KEYWORDS.has(lower) || CSS_NAMED_COLORS.has(lower)) return true
  const gradientOk = prop === 'background' || prop === 'backgroundImage'
  if (names.colors.has(s) || (gradientOk && names.gradients.has(s))) return true
  const m = s.match(SMBLS_COLOR_TOKEN)
  if (!m) return false
  return names.colors.has(m[1]) || (gradientOk && names.gradients.has(m[1]))
}

// Keys whose string value is CONTENT, never a style (a `var(` in a label is text).
const CONTENT_KEYS = new Set([
  'text',
  'html',
  'placeholder',
  'label',
  'title',
  'alt',
  'href',
  'src',
  'name',
  'key',
  'id',
  'value',
  'content',
  'aria-label',
  'ariaLabel'
])

// ── Hgroup (GENERATED-VIEW-HGROUP-PLACEHOLDER-TEXT-1) ─────────────────────────
// An Hgroup draws ONLY its own two slots — `H` (the heading) and `P` (the line
// under it); it has no `title` / `subtitle` / `children` props. Measured on dev
// 2026-10-05 (Storm): generated views wrote `{ extends: 'Hgroup', title: …,
// subtitle: … }` and `{ extends: 'Hgroup', children: [ … ] }`; the workspace's
// Hgroup (the brand's) ships placeholder copy in both slots, so the view showed
// "Heading" / "Paragraph" — and an Hgroup whose slots hide themselves when
// empty showed nothing at all.
//   hgroup-empty        neither slot is filled — nothing (or only placeholder
//                       copy) would show; `detail` names what was written
//                       instead (`title`, `children`, …) when anything was
//   hgroup-placeholder  one slot is filled, the other is neither filled nor
//                       hidden and the LIVE Hgroup's slot carries default copy
//                       (`detail` "P: Paragraph") — judged only with the live
//                       components (opts.components), never a guess
const HGROUP_STAND_INS = ['title', 'subtitle', 'heading', 'subheading', 'description', 'children']
const _extendsNames = (v) =>
  (Array.isArray(v) ? v : [v])
    .filter((n) => typeof n === 'string')
    .map((n) => n.split('>')[0].trim())
const _hgroupBase = (node) => _extendsNames(node.extends).find((n) => /^Hgroup/.test(n)) || null
const _slotFilled = (s) => {
  if (typeof s === 'string') return !!s.trim()
  if (typeof s === 'number') return true
  if (!s || typeof s !== 'object' || Array.isArray(s)) return false
  if (s.text != null || s.html != null) return true
  return Object.keys(s).some((k) => /^[A-Z]/.test(k))
}
const _slotHidden = (s) =>
  s === null ||
  s === false ||
  (!!s &&
    typeof s === 'object' &&
    (s.hide === true ||
      typeof s.hide === 'function' ||
      s.if === false ||
      typeof s.if === 'function' ||
      s.display === 'none'))
// The live slot's default copy — a placeholder the slot shows when the view
// leaves it — unless the live slot hides itself (`if`) when nothing fills it.
const _slotDefaultText = (components, base, slot) => {
  const live = components && typeof components === 'object' ? components[base] : null
  const s = live && typeof live === 'object' ? live[slot] : null
  if (!s || typeof s !== 'object' || s.if != null) return ''
  return typeof s.text === 'string' ? s.text.trim() : ''
}
const _hgroupViolations = (node, path, components) => {
  const base = _hgroupBase(node)
  if (!base) return []
  const at = path.join('.') || '(root)'
  const filledH = _slotFilled(node.H)
  const filledP = _slotFilled(node.P)
  if (!filledH && !filledP) {
    const instead = HGROUP_STAND_INS.filter((k) => node[k] != null)
    return [{ rule: 'hgroup-empty', at, detail: instead.join(', ') || base }]
  }
  const out = []
  for (const [slot, filled] of [
    ['H', filledH],
    ['P', filledP]
  ]) {
    if (filled || _slotHidden(node[slot])) continue
    const copy = _slotDefaultText(components, base, slot)
    if (copy)
      out.push({ rule: 'hgroup-placeholder', at: `${at}.${slot}`, detail: `${slot}: ${copy}` })
  }
  return out
}

// Every CSS variable a style string reads: `var(--x)` anywhere, or smbls's
// bare `--x` shorthand for the whole value.
const _cssVarsOf = (value) => {
  const names = []
  const re = /var\(\s*(--[A-Za-z0-9_-]+)/g
  let m
  while ((m = re.exec(value))) names.push(m[1])
  const bare = value.trim().match(/^(--[A-Za-z0-9_-]+)$/)
  if (bare) names.push(bare[1])
  return names
}

// ── Palette fill under somebody else's ink (GENERATED-VIEW-PALETTE-FILL-AND-UNKNOWN-ICON-1)
// A theme pairs a background with an ink. A palette `background` /
// `backgroundColor` replaces only the background, so whatever ink was in force
// stays over the new fill — the class the ticket measured. The fill is fine
// when something pairs it: a theme whose OWN background is that same fill, an
// ink the element sets for itself, or an ink every text under it sets.
const FILL_PROPS = new Set(['background', 'backgroundColor'])

// The design system's neutral SURFACE roles — a plate colour, not an accent.
// They are made to sit under the current ink, so a fill with one of them keeps
// the pairing. Only names the live system carries are ever exempt.
const NEUTRAL_FILL_ROLES = new Set(['overlay', 'highlight', 'hover', 'line', 'shadow', 'blur'])

// Copy a plate carries itself. A function is content too (it computes text).
const TEXT_KEYS = ['text', 'html']
const _carriesOwnText = (node) => {
  for (const key of TEXT_KEYS) {
    const value = node[key]
    if (typeof value === 'string' ? !!value.trim() : value != null) return true
  }
  return false
}

// An element brings its OWN PAIR when it sets a theme: a theme owns the
// background and the ink together, so it is its own plate and never reads the
// fill above it. A bare `color` is NOT a pair — it is one ink over somebody
// else's fill, and the measured view proves the difference: white over
// cerulean-10 still reads 2.4:1, under the 3:1 floor a large title needs.
const _bringsOwnPair = (node) => typeof node.theme === 'string' && !!node.theme.trim()

// The path of the first text under `node` that no element between the fill and
// that text pairs an ink with; null when every text is paired (or there is
// none). `depth` 0 is the fill's own element — its theme is exactly what the
// fill overrode, so it never pairs the branch.
const _unlitText = (node, path, depth) => {
  if (!node || typeof node !== 'object') return null
  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i++) {
      const hit = _unlitText(node[i], path.concat(String(i)), depth + 1)
      if (hit) return hit
    }
    return null
  }
  if (depth > 0 && _bringsOwnPair(node)) return null
  if (_carriesOwnText(node)) return path.join('.') || '(root)'
  for (const key of Object.keys(node)) {
    if (DATA_BAGS.has(key)) continue
    const value = node[key]
    if (!value || typeof value !== 'object') continue
    const hit = _unlitText(value, path.concat(key), depth + 1)
    if (hit) return hit
  }
  return null
}

// Every background a theme names, across its scheme blocks ('@dark', '@light')
// and its nested states — the fills that theme already pairs with an ink.
const _themeBackgrounds = (theme) => {
  const names = new Set()
  const visit = (value, depth) => {
    if (!value || typeof value !== 'object' || Array.isArray(value) || depth > 4) return
    for (const key of Object.keys(value)) {
      const v = value[key]
      if (FILL_PROPS.has(key) && typeof v === 'string') names.add(v.trim())
      else visit(v, depth + 1)
    }
  }
  visit(theme, 0)
  return names
}

const _themeFills = (ds) => {
  const themes = ds && (ds.theme || ds.THEME)
  if (!themes || typeof themes !== 'object') return null
  const out = {}
  for (const name of Object.keys(themes)) out[name] = _themeBackgrounds(themes[name])
  return out
}

// The live design system's own name for this fill, or null when the value is
// not a palette colour: a CSS colour, a keyword, a gradient and an unknown
// name are all somebody else's rule ('unknown-color' judges the last one).
const _paletteFillName = (value, names) => {
  if (typeof value !== 'string') return null
  const s = value.trim()
  if (!s || CSS_COLOR_SYNTAX.test(s)) return null
  if (CSS_COLOR_KEYWORDS.has(s.toLowerCase())) return null
  if (names.colors.has(s)) return s
  const m = s.match(SMBLS_COLOR_TOKEN)
  return m && names.colors.has(m[1]) ? m[1] : null
}

// ── Icon names (GENERATED-VIEW-PALETTE-FILL-AND-UNKNOWN-ICON-1) ──────────────
// `toCamelCase` of @symbo.ls/utils (packages/utils/string-extra.js), for a
// caller with no live utils to lend; `opts.toCamelCase` wins when there is one,
// so the gate resolves a name with the very function the framework uses.
const _toCamelCase = (str) =>
  String(str == null ? '' : str)
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
      index === 0 ? word.toLowerCase() : word.toUpperCase()
    )
    .replace(/\s+/g, '')

// An Icon: a literal `extends: 'Icon'`, or DOMQL's key-based auto-extend
// (`Icon: {…}`, `Icon_1: {…}` resolve to the registered component).
const _isIconNode = (node, key) =>
  _extendsNames(node.extends).includes('Icon') || /^Icon(_|$)/.test(String(key == null ? '' : key))

// The framework's own resolution, mirrored (default-config components/Icon
// `applyIconRender`): the variant-suffix join ('deviceBigScreen outline' →
// 'deviceBigScreenOutline'), `toCamelCase`, then `icons[camel]`,
// `icons[seg0+seg1]`, `icons[seg0]`.
const _iconLookup = (raw, camelOf) => {
  let name = raw.trim()
  if (name.includes(' ')) {
    const parts = name.split(/\s+/)
    name =
      parts[0] +
      parts
        .slice(1)
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join('')
  }
  const camel = camelOf(name)
  const seg = camel.split(/([a-z])([A-Z])/g)
  return [camel, seg[0] + seg[1], seg[0]]
}

const _iconResolves = (raw, icons, camelOf) => {
  if (!raw.trim()) return true
  return _iconLookup(raw, camelOf).some((k) => !!icons[k])
}

// `semanticIcons` answers a whole family by its root word (an SF Symbols name),
// so a name that family covers paints and is never refused.
const _semanticIconCovers = (raw, semanticIcons, camelOf) => {
  if (!semanticIcons || typeof semanticIcons !== 'object') return false
  const camel = camelOf(raw)
  const seg = camel.split(/([a-z])([A-Z])/g)
  const root = seg[1] ? seg[0] : raw.split('.')[0].split(' ')[0]
  return !!semanticIcons[root]
}

// The registered names nearest a miss: the bare name after a `library/` prefix
// when THAT one is registered (the resolver never strips the prefix itself),
// then the registered names that share the miss's own word, shortest first.
const _nearIcons = (raw, icons, camelOf, max = 3) => {
  const out = []
  const push = (n) => {
    if (n && !out.includes(n) && out.length < max) out.push(n)
  }
  const slash = raw.indexOf('/')
  if (slash > 0) {
    const bare = raw.slice(slash + 1).trim()
    if (bare && _iconResolves(bare, icons, camelOf)) push(bare)
  }
  // compared on letters alone, so 'cloud-outline' finds 'cloudOutline': the
  // separators a model guesses are exactly what a name match must ignore.
  const plain = (s) =>
    camelOf(s)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
  const needle = plain(raw.replace(/^[^/]*\//, ''))
  if (needle.length >= 3) {
    Object.keys(icons)
      .filter((k) => {
        const l = plain(k)
        return l === needle || l.includes(needle) || (l.length >= 3 && needle.includes(l))
      })
      .sort((a, b) => a.length - b.length || (a < b ? -1 : 1))
      .forEach(push)
  }
  return out
}

/**
 * The style violations of a parsed generated view: `[{ rule, at, detail }]`,
 * `rule` ∈ 'unitless-size' | 'grid-track' | 'unknown-color' | 'unknown-css-var' |
 * 'unknown-theme' | 'size-word' | 'grid-cols' | 'hgroup-empty' | 'hgroup-placeholder' |
 * 'palette-fill' | 'unknown-icon', `at` the element path ('Row.0.Track.height').
 * `opts.toCamelCase` lends the framework's own name transform to the icon rule.
 */
export function generatedViewStyleLint(def, designSystem, opts = {}) {
  const names = designSystemColorNames(designSystem)
  const themes = _keysOf(designSystem && (designSystem.theme || designSystem.THEME))
  const themeNames = themes.length ? new Set(themes) : null
  const sizesTable = designSystem && (designSystem.sizes || designSystem.SIZES)
  const sizes = sizesTable && typeof sizesTable === 'object' ? sizesTable : null
  // (name) => true | false | null — null when the page cannot say (never judged)
  const cssVarDefined = typeof opts.cssVarDefined === 'function' ? opts.cssVarDefined : null
  // the live component registry — the Hgroup rule reads its slots' default copy
  const components = opts.components && typeof opts.components === 'object' ? opts.components : null
  // the backgrounds each theme already pairs with an ink ('palette-fill')
  const themeFills = _themeFills(designSystem)
  // the live icon set ('unknown-icon'). An `iconsLoader` may register a name
  // after boot, so with one in place no icon name is judged.
  const iconsTable = designSystem && (designSystem.icons || designSystem.ICONS)
  const icons =
    iconsTable &&
    typeof iconsTable === 'object' &&
    Object.keys(iconsTable).length &&
    typeof (designSystem.iconsLoader || designSystem.ICONS_LOADER) !== 'function'
      ? iconsTable
      : null
  const semanticIcons = designSystem && designSystem.semanticIcons
  const camelOf = typeof opts.toCamelCase === 'function' ? opts.toCamelCase : _toCamelCase
  const out = []
  const seen = new WeakSet()
  const walk = (node, path) => {
    if (!node || typeof node !== 'object' || seen.has(node)) return
    seen.add(node)
    if (Array.isArray(node)) {
      node.forEach((item, i) => walk(item, path.concat(String(i))))
      return
    }
    out.push(..._hgroupViolations(node, path, components))
    if (node.cols != null && typeof node.cols !== 'function' && _gridLike(node)) {
      out.push({
        rule: 'grid-cols',
        at: path.concat('cols').join('.'),
        detail: `cols: ${node.cols}`
      })
    }
    // A palette fill on a plate that carries text, with no ink paired to it.
    if (names) {
      for (const key of ['background', 'backgroundColor']) {
        const fill = _paletteFillName(node[key], names)
        if (!fill || NEUTRAL_FILL_ROLES.has(fill)) continue
        const pairedByTheme =
          themeFills &&
          typeof node.theme === 'string' &&
          node.theme
            .trim()
            .split(/\s+/)
            .some((t) => themeFills[t] && themeFills[t].has(String(node[key]).trim()))
        if (pairedByTheme) continue
        if (!_unlitText(node, path, 0)) continue
        out.push({
          rule: 'palette-fill',
          at: path.concat(key).join('.') || key,
          detail: `${key}: ${String(node[key]).trim()}`
        })
      }
    }
    // An Icon name the live design system does not register.
    if (icons && _isIconNode(node, path[path.length - 1]) && node.src == null) {
      const raw = typeof node.name === 'string' ? node.name : node.icon
      if (typeof raw === 'string' && raw.trim() && !raw.includes('{{')) {
        if (
          !_iconResolves(raw, icons, camelOf) &&
          !_semanticIconCovers(raw, semanticIcons, camelOf)
        ) {
          const near = _nearIcons(raw, icons, camelOf)
          out.push({
            rule: 'unknown-icon',
            at: path.concat(typeof node.name === 'string' ? 'name' : 'icon').join('.'),
            detail: raw.trim(),
            ...(near.length ? { near: near.join(', ') } : {})
          })
        }
      }
    }
    const dataRows = node.childrenAs === 'state'
    for (const key of Object.keys(node)) {
      const value = node[key]
      if (DATA_BAGS.has(key)) continue
      if (key === 'children' && dataRows) continue
      // checked BEFORE the walk: an array / object on a track key is the error
      // itself, never a child to descend into
      if (TRACK_PROPS.has(key) && _isBadTrack(value)) {
        out.push({
          rule: 'grid-track',
          at: path.concat(key).join('.'),
          detail: `${key}: ${typeof value === 'object' ? (Array.isArray(value) ? '[…]' : '{…}') : value}`
        })
        continue
      }
      if (value && typeof value === 'object') {
        walk(value, path.concat(key))
        continue
      }
      if (cssVarDefined && typeof value === 'string' && !CONTENT_KEYS.has(key)) {
        const missing = _cssVarsOf(value).filter((v) => cssVarDefined(v) === false)
        if (missing.length) {
          for (const v of missing) {
            // the near miss the page DOES define ('--purple' → --color-purple)
            const bare = v.replace(/^--(color-|spacing-)?/, '')
            const near = ['--color-' + bare, '--spacing-' + bare].find(
              (c) => c !== v && cssVarDefined(c) === true
            )
            out.push({
              rule: 'unknown-css-var',
              at: path.concat(key).join('.'),
              detail: v,
              ...(near ? { near } : {})
            })
          }
          continue
        }
      }
      if (
        key === 'theme' &&
        themeNames &&
        typeof value === 'string' &&
        value.trim() &&
        !themeNames.has(value.trim().split(/\s+/)[0])
      ) {
        out.push({ rule: 'unknown-theme', at: path.concat(key).join('.'), detail: value.trim() })
      } else if (BOX_SIZE_PROPS.has(key) && _sizeWord(value, sizes)) {
        out.push({
          rule: 'size-word',
          at: path.concat(key).join('.'),
          detail: `${key}: ${value.trim()}`
        })
      } else if (LENGTH_PROPS.has(key) && _isBareNumber(value)) {
        out.push({
          rule: 'unitless-size',
          at: path.concat(key).join('.'),
          detail: `${key}: ${value}`
        })
      } else if (
        names &&
        COLOR_PROPS.has(key) &&
        typeof value === 'string' &&
        !_isKnownColor(key, value, names)
      ) {
        out.push({ rule: 'unknown-color', at: path.concat(key).join('.'), detail: value.trim() })
      }
    }
  }
  walk(def, [])
  return out
}

// A few of the live design system's own colour names for the hint (never a
// hardcoded palette): its hue names first (the ones CSS also knows — blue,
// green, red, …), then its short lowercase names (title, caption, …); never a
// camelCase app colour or a numbered grey.
// The usual semantic roles, offered first when the live design system has
// them (text: title / caption / paragraph; lines and surfaces: line / overlay /
// highlight / hover) — a model that wrote 'surface' needs a ROLE, not a hue.
// Only names the live system carries are ever offered.
const SEMANTIC_ROLES = ['title', 'caption', 'paragraph', 'line', 'overlay', 'highlight', 'hover']

export const designSystemColorExamples = (designSystem, max = 8) => {
  const names = designSystemColorNames(designSystem)
  if (!names) return []
  const roles = SEMANTIC_ROLES.filter((n) => names.colors.has(n)).slice(0, Math.ceil(max / 2))
  const hues = [...names.colors].filter(
    (n) => CSS_NAMED_COLORS.has(n) && !/^(gr[ae]y|black|white)/.test(n)
  )
  return [...roles, ...hues].slice(0, max)
}

// THE NAMES A GENERATED VIEW MAY USE, for the assistant's UI catalog
// (UI-CATALOG-CARRIES-NO-DESIGN-SYSTEM-NAMES-1). Measured on dev 2026-10-04/05
// (Storm): answer views guessed 'surface', 'warning.text', a `theme:
// 'paragraph'` and invented CSS variables — names the lint refuses only after
// the call. Told the live names up front, the first call can be right. Read
// from the LIVE design system the lint judges against (the element context's),
// never a hardcoded palette:
//   colors  the semantic roles first, then the hues CSS also knows, then the
//           system's other plain lowercase names — never a camelCase app colour
//           or a numbered shade (they are the system's internals);
//   themes  the system's plain lowercase theme names.
// Capped: the catalog rides every turn.
export const designSystemNames = (designSystem, { maxColors = 32, maxThemes = 24 } = {}) => {
  const names = designSystemColorNames(designSystem)
  const colors = []
  if (names) {
    const all = [...names.colors]
    const push = (n) => {
      if (!colors.includes(n)) colors.push(n)
    }
    SEMANTIC_ROLES.filter((n) => names.colors.has(n)).forEach(push)
    all.filter((n) => CSS_NAMED_COLORS.has(n)).forEach(push)
    all.filter((n) => /^[a-z]+(-[a-z]+)?$/.test(n)).forEach(push)
  }
  const themes = _keysOf(designSystem && (designSystem.theme || designSystem.THEME)).filter((n) =>
    /^[a-z]+$/.test(n)
  )
  return { colors: colors.slice(0, maxColors), themes: themes.slice(0, maxThemes) }
}

// The live page's CSS variables, for the `unknown-css-var` rule: true when the
// document root defines the variable, false when it does not, null when there
// is no document to ask (a test, a worker) — never a guess.
export const pageCssVarResolver = () => {
  const doc = typeof globalThis !== 'undefined' ? globalThis.document : null
  const gcs = typeof globalThis !== 'undefined' ? globalThis.getComputedStyle : null
  if (!doc || !doc.documentElement || typeof gcs !== 'function') return null
  let style = null
  return (name) => {
    try {
      if (!style) style = gcs(doc.documentElement)
      return !!String(style.getPropertyValue(name) || '').trim()
    } catch (_) {
      return null
    }
  }
}
